<!-- Loading state -->
<div *ngIf="!(apiLoaded$ | async)" class="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
  <div class="text-center">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
    <p class="text-gray-600">Loading map...</p>
  </div>
</div>

<!-- Error state (you can add an error observable to show this) -->
<!-- <div *ngIf="mapError$ | async as error" class="w-full h-full flex items-center justify-center bg-red-50 rounded-lg">
  <div class="text-center p-4">
    <div class="text-red-500 mb-2">
      <i class="fas fa-exclamation-triangle text-2xl"></i>
    </div>
    <p class="text-red-700 font-medium">Failed to load map</p>
    <p class="text-red-600 text-sm mt-1">{{ error }}</p>
    <button
      (click)="retryInitialization()"
      class="mt-3 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors">
      Retry
    </button>
  </div>
</div> -->

<!-- Main map container -->
<div *ngIf="apiLoaded$ | async" [class.ig-cluster-map]="useCluster" class="w-full h-full" [attr.data-map-mode]="_mode">

  <!-- Dark mode map -->
  @if (_mode === 'dark') {
  <google-map [center]="center" [options]="options" (mapClick)="mapClick($event)"
    [attr.data-testid]="'google-map-dark'">

    <!-- Directions renderer -->
    @if (directionsResults$ | async; as directionsResults) {
    <map-directions-renderer [directions]="directionsResults" [suppressMarkers]="true"
      [options]="directionsMarkerOptions" />
    }

    <!-- Clustered markers -->
    <!--TODO: Upgrade to markerclusterer because markerclustererplus is deprecated-->
    <!-- change imagePath from https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m to https://superstorefinder.net/support/wp-content/uploads/2015/07/m1.png because the original image is not available (m2.png) -->
    @if (useCluster) {
    <map-marker-clusterer [imagePath]="'https://superstorefinder.net/support/wp-content/uploads/2015/07/m'"
      (clusteringend)="clustererEnd($event)">
      <map-marker #markerElem="mapMarker" *ngFor="let marker of _markers; trackBy: trackByMarkerId"
        [position]="marker.position" [label]="marker.label" [title]="marker.title" [options]="marker.options"
        (mapClick)="markerClick(markerElem, marker)">
      </map-marker>
    </map-marker-clusterer>
    } @else {
    <!-- Non-clustered markers -->
    <map-marker #markerElem="mapMarker" *ngFor="let marker of _markers; trackBy: trackByMarkerId"
      [position]="marker.position" [label]="marker.label" [title]="marker.title" [options]="marker.options"
      (mapClick)="markerClick(markerElem, marker)">
    </map-marker>
    }

    <!-- Info window -->
    <map-info-window>
      <!-- Event info window -->
      <div *ngIf="isShowEventInfoWindow && selectedMarker" (click)="goToEvent(selectedMarker.marker.data)"
        class="relative transition-all ig-map-info-window" [class.cursor-pointer]="canInfoWindowClick" [attr.data-testid]="'event-info-window'">
        <ig-image [src]="selectedMarker?.marker?.data?.cover?.source" class="block overflow-hidden"
          cls="aspect-[3/2] w-full transition-all" [alt]="selectedMarker?.marker?.data?.name || 'Event image'">
        </ig-image>
        <div class="px-4 py-1 bg-white rounded-b-3xl">
          <div class="ig-map-info-win-name text-sm font-semibold text-gray-600 mb-1 ellipsis">
            {{ selectedMarker?.marker?.data?.name }}
          </div>
          <div class="text-xs text-gray-500 leading-3">
            <span>{{ selectedMarker?.marker.data?.timeTwoLines?.date }}</span>
            <span
              *ngIf="selectedMarker?.marker.data?.eventTimes?.length > 1 || selectedMarker?.marker.data?.timeTwoLines?.time"
              class="mx-2">·</span>
            <span>{{
              selectedMarker?.marker?.data?.eventTimes?.length > 1 ? 'Multiple times'
              : selectedMarker?.marker?.data?.timeTwoLines?.time
              }}</span>
          </div>
        </div>
      </div>

      <!-- Place info window -->
      <div *ngIf="isShowPlaceInfoWindow && selectedMarker" (click)="goToPlace(selectedMarker.marker.data)"
        class="rounded-lg relative transition-all ig-map-info-window" [class.cursor-pointer]="canInfoWindowClick"
        [attr.data-testid]="'place-info-window'">
        <ig-image [src]="selectedMarker?.marker?.data?.cover?.source" cls="aspect-[1/1] rounded-lg"
          [alt]="selectedMarker?.marker?.data?.name || 'Place image'">
        </ig-image>
        <div class="w-full h-1/2 absolute bottom-0 left-0 rounded-lg bg-gradient-to-b from-transparent to-black">
          <div class="ig-map-info-title absolute bottom-0 w-full p-4 mobile:pb-2">
            <p class="text-sm text-white font-semibold leading-[1.25rem] mb-1">
              {{ selectedMarker?.marker?.data?.name }}
            </p>
            <p class="ig-map-info-category text-sm text-gray-300 leading-[1.15rem] max-lines-2">
              {{ getDisplayCategory(selectedMarker?.marker?.data) }}
            </p>
          </div>
        </div>
      </div>
    </map-info-window>
  </google-map>

  } @else {
  <!-- Light mode map -->
  <google-map [center]="center" [options]="options" (mapClick)="mapClick($event)"
    [attr.data-testid]="'google-map-light'">

    <!-- Directions renderer -->
    @if (directionsResults$ | async; as directionsResults) {
    <map-directions-renderer [directions]="directionsResults" [suppressMarkers]="true"
      [options]="directionsMarkerOptions" />
    }

    <!-- Clustered markers -->
    @if (useCluster) {
    <map-marker-clusterer (clusteringend)="clustererEnd($event)"
      [imagePath]="'https://superstorefinder.net/support/wp-content/uploads/2015/07/m'"
      [attr.data-testid]="'marker-clusterer'">
      <map-marker #markerElem="mapMarker" *ngFor="let marker of _markers; trackBy: trackByMarkerId"
        [position]="marker.position" [label]="marker.label" [title]="marker.title" [options]="marker.options"
        (mapClick)="markerClick(markerElem, marker)" [attr.data-marker-id]="marker.data?._id">
      </map-marker>
    </map-marker-clusterer>
    } @else {
    <!-- Non-clustered markers -->
    <map-marker #markerElem="mapMarker" *ngFor="let marker of _markers; trackBy: trackByMarkerId"
      [position]="marker.position" [label]="marker.label" [title]="marker.title" [options]="marker.options"
      (mapClick)="markerClick(markerElem, marker)" [attr.data-marker-id]="marker.data?._id">
    </map-marker>
    }

    <!-- Info window -->
    <map-info-window>
      <!-- Event info window -->
      <div *ngIf="isShowEventInfoWindow && selectedMarker" (click)="goToEvent(selectedMarker.marker.data)"
        class="relative transition-all cursor-pointer ig-map-info-window" [attr.data-testid]="'event-info-window'">
        <ig-image [src]="selectedMarker?.marker?.data?.cover?.source" class="block overflow-hidden"
          cls="aspect-[3/2] w-full transition-all" [alt]="selectedMarker?.marker?.data?.name || 'Event image'">
        </ig-image>
        <div class="px-4 py-1 bg-white rounded-b-3xl">
          <div class="ig-map-info-win-name text-sm font-semibold text-gray-600 mb-1 ellipsis">
            {{ selectedMarker?.marker?.data?.name }}
          </div>
          <div class="text-xs text-gray-500 leading-3">
            <span>{{ selectedMarker?.marker.data?.timeTwoLines?.date }}</span>
            <span
              *ngIf="selectedMarker?.marker.data?.eventTimes?.length > 1 || selectedMarker?.marker.data?.timeTwoLines?.time"
              class="mx-2">·</span>
            <span>{{
              selectedMarker?.marker?.data?.eventTimes?.length > 1 ? 'Multiple times'
              : selectedMarker?.marker?.data?.timeTwoLines?.time
              }}</span>
          </div>
        </div>
      </div>

      <!-- Place info window -->
      <div *ngIf="isShowPlaceInfoWindow && selectedMarker" (click)="goToPlace(selectedMarker.marker.data)"
        class="rounded-lg relative transition-all cursor-pointer ig-map-info-window"
        [attr.data-testid]="'place-info-window'">
        <ig-image [src]="selectedMarker?.marker?.data?.cover?.source" cls="aspect-[1/1] rounded-lg"
          [alt]="selectedMarker?.marker?.data?.name || 'Place image'">
        </ig-image>
        <div class="w-full h-1/2 absolute bottom-0 left-0 rounded-lg bg-gradient-to-b from-transparent to-black">
          <div class="ig-map-info-title absolute bottom-0 w-full p-4 mobile:pb-2">
            <p class="text-sm text-white font-semibold leading-[1.25rem] mb-1">
              {{ selectedMarker?.marker?.data?.name }}
            </p>
            <p class="ig-map-info-category text-sm text-gray-300 leading-[1.15rem] max-lines-2">
              {{ getDisplayCategory(selectedMarker?.marker?.data) }}
            </p>
          </div>
        </div>
      </div>
    </map-info-window>
  </google-map>
  }
</div>

<!-- Fallback for no markers -->
<!-- <div *ngIf="(apiLoaded$ | async) && _markers.length === 0"
  class="absolute inset-0 flex items-center justify-center pointer-events-none">
  <div class="text-center text-gray-500">
    <i class="fas fa-map-marker-alt text-3xl mb-2"></i>
    <p>No locations to display</p>
  </div>
</div> -->
